import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { VCS_APP_DISPLAY } from '../../../../../shared/vcs/githubApp/apps';
import { useConnectGitHubAppInstallation } from '../../../../hooks/api/vcs/githubApp/useConnectGitHubAppInstallation';
import { useGitHubAppConnectCandidates } from '../../../../hooks/api/vcs/githubApp/useGitHubAppConnectCandidates';
import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import Flex from '../../../generic/Flex';
import Loader from '../../../generic/Loader';
import type { VcsApp } from '../../../../../shared/vcs/githubApp/apps';

import styles from './VcsConnectAccountModal.module.less';

type Props = {
  show: boolean;
  app: VcsApp;
  /** True when GitHub authorization failed, so the modal explains rather than lists. */
  hasAuthorizationError: boolean;
  onHide: () => void;
  /** Goes to GitHub's install page, for an account that does not have the app yet. */
  onInstallAnother: () => void;
};

/**
 * Shown when the user comes back from GitHub authorization with installations their account
 * already has for the app: they connect one to this org, or install the app on another account.
 */
export const VcsConnectAccountModal: VoidFunctionComponent<Props> = ({
  show,
  app,
  hasAuthorizationError,
  onHide,
  onInstallAnother,
}) => {
  const candidates = useGitHubAppConnectCandidates(app, { enabled: show && !hasAuthorizationError });
  const connect = useConnectGitHubAppInstallation(app);
  const appName = VCS_APP_DISPLAY[app].name;

  if (!show) {
    return null;
  }

  const renderBody = () => {
    if (hasAuthorizationError) {
      return <ErrorAlert status={{ error: 'GitHub authorization was not completed. Please try again.' }} />;
    }
    if (candidates.isLoading) {
      return <Loader />;
    }
    if (candidates.isError) {
      return <ErrorAlert status={{ error: 'Failed to load your GitHub installations. Please try again.' }} />;
    }
    const results = candidates.data?.results ?? [];
    if (results.length === 0) {
      return (
        <p className={styles.empty}>
          We couldn&apos;t find any GitHub accounts to connect. Try again, or install the {appName} app
          on another account.
        </p>
      );
    }
    return (
      <>
        <p className={styles.intro}>
          These GitHub accounts already have the {appName} app installed. Connect one to import its
          repositories into this organization, or install the app on another account.
        </p>
        <ul className={styles.list}>
          {results.map((installation) => (
            <li key={installation.gitHubInstallationId} className={styles.row}>
              {installation.avatarUrl && <img className={styles.avatar} src={installation.avatarUrl} alt="" />}
              <div className={styles.account}>
                <span className={styles.name}>{installation.accountName}</span>
                <span className={styles.type}>{installation.accountType}</span>
              </div>
              {installation.isConnected ? (
                <span className={styles.connected}>Connected</span>
              ) : (
                <Button
                  type={BUTTON_TYPE.PRIMARY}
                  size={BUTTON_SIZE.SMALL}
                  isDisabled={connect.isPending}
                  onClick={() => connect.mutate(installation.gitHubInstallationId)}
                >
                  Connect
                </Button>
              )}
            </li>
          ))}
        </ul>
      </>
    );
  };

  return (
    <Modal show onHide={onHide} aria-labelledby="vcs-connect-account-modal-title">
      <Modal.Header closeButton>
        <Modal.Title id="vcs-connect-account-modal-title">Choose a GitHub account</Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderBody()}</Modal.Body>
      <Modal.Footer>
        <Flex gap="8px" justify="flex-end">
          <Button type={BUTTON_TYPE.SECONDARY} onClick={onInstallAnother}>Install on another account</Button>
          <Button type={BUTTON_TYPE.SECONDARY} onClick={onHide}>Close</Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};
